import { useState } from 'react';
import { Toggle } from '../../../shared/components/Toggle';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { ShippingMode, type ShippingModeState } from '../api/shippingZonesApi';

interface Props {
  mode: ShippingModeState | undefined;
  isLoading: boolean;
  onSetMode: (mode: ShippingMode) => void;
  isSaving: boolean;
  canManage: boolean;
}

/**
 * COURIER vs ZONE_BASED switch (PE-2172/PE-2173).
 *
 * A confirmation gate on flip is deliberate: switching modes changes what customers
 * are quoted at checkout immediately, with nothing in the UI to undo a fat-fingered
 * toggle beyond flipping it back.
 */
export function ShippingModeCard({ mode, isLoading, onSetMode, isSaving, canManage }: Props) {
  const [pendingMode, setPendingMode] = useState<ShippingMode | null>(null);

  const current = mode?.mode ?? ShippingMode.COURIER;
  const isZoneBased = current === ShippingMode.ZONE_BASED;

  const requestFlip = (checked: boolean) => {
    setPendingMode(checked ? ShippingMode.ZONE_BASED : ShippingMode.COURIER);
  };

  const confirmFlip = () => {
    if (pendingMode) onSetMode(pendingMode);
    setPendingMode(null);
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-6 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[16px] font-semibold text-foreground">Shipping mode</h2>
          <p className="text-[13px] text-muted-foreground mt-1 max-w-2xl">
            Zone rates only price checkout when mode is Zone-based; Local pickup is offered in both
            modes. Courier mode quotes live rates from this merchant's connected courier providers
            instead of the zones below.
          </p>
        </div>
        <Toggle
          label={isZoneBased ? 'Zone-based' : 'Courier'}
          checked={isZoneBased}
          onChange={canManage ? requestFlip : () => {}}
        />
      </div>
      {isLoading && <p className="text-[12px] text-muted-foreground mt-3">Loading current mode…</p>}

      <Modal isOpen={pendingMode !== null} onClose={() => setPendingMode(null)} size="sm">
        <div className="p-8">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Switch to {pendingMode === ShippingMode.ZONE_BASED ? 'Zone-based' : 'Courier'} shipping?
          </h3>
          <p className="text-[14px] text-muted-foreground mb-6">
            {pendingMode === ShippingMode.ZONE_BASED
              ? 'Checkout will price shipping from the zones below instead of live courier rates.'
              : 'Checkout will go back to quoting live rates from connected courier providers. Zone rates stay saved but stop applying.'}
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={() => setPendingMode(null)}>
              Cancel
            </Button>
            <Button type="button" size="sm" className="h-[40px]" onClick={confirmFlip} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
